import React from "react";
import { ArrowRight, FileText, Gear, Wallet, GitBranch, Prohibit, Scroll, Bank } from "@phosphor-icons/react";
import { govActionLabel, govStatusTone } from "@/lib/governance";
import { Badge } from "@/components/primitives";
import { cn } from "@/lib/format";

const TYPE_META = {
  info_action: { icon: FileText, color: "#22d3ee" },
  treasury_withdrawals: { icon: Wallet, color: "#e879f9" },
  parameter_change: { icon: Gear, color: "#a855f7" },
  hard_fork_initiation: { icon: GitBranch, color: "#f59e0b" },
  no_confidence: { icon: Prohibit, color: "#f87171" },
  new_constitution: { icon: Scroll, color: "#34d399" },
  new_committee: { icon: Bank, color: "#38bdf8" },
  _default: { icon: FileText, color: "#22d3ee" },
};

const outcomeOf = (r) => {
  if (r.enacted_epoch) return { text: `enacted ${r.enacted_epoch}`, epoch: r.enacted_epoch, color: "#34d399", done: true };
  if (r.ratified_epoch) return { text: `ratified ${r.ratified_epoch}`, epoch: r.ratified_epoch, color: "#34d399", done: true };
  if (r.expired_epoch) return { text: `expired ${r.expired_epoch}`, epoch: r.expired_epoch, color: "#f472b6", done: true };
  if (r.dropped_epoch) return { text: `dropped ${r.dropped_epoch}`, epoch: r.dropped_epoch, color: "#f87171", done: true };
  return { text: "—", epoch: null, color: "#94a3b8", done: false };
};

const progOf = (r, currentEpoch) => {
  const oc = outcomeOf(r);
  if (oc.done && r.submitted_epoch != null) {
    return { start: r.submitted_epoch, end: oc.epoch, fill: 1, pct: 100, color: oc.color };
  }
  if (r.expires_after_epoch != null && r.submitted_epoch != null && currentEpoch != null) {
    const span = r.expires_after_epoch - r.submitted_epoch;
    const f = span > 0 ? Math.max(0, Math.min(1, (currentEpoch - r.submitted_epoch) / span)) : 0;
    return { start: r.submitted_epoch, end: r.expires_after_epoch, fill: f, pct: Math.round(f * 100), color: "#22d3ee" };
  }
  return { start: r.submitted_epoch ?? "—", end: null, fill: 0, pct: 0, color: "#94a3b8" };
};

const ProgressBar = ({ p }) => (
  <div className="flex items-center gap-2.5 w-full">
    <span className="text-[12px] mono tabnum text-muted-foreground shrink-0 w-[34px] text-right">{p.start}</span>
    <div className="relative h-1.5 rounded-full bg-white/[0.08] flex-1 min-w-[40px] overflow-hidden">
      <div className="absolute inset-y-0 left-0 rounded-full" style={{ width: `${p.fill * 100}%`, background: `linear-gradient(90deg, ${p.color}99, ${p.color})`, boxShadow: `0 0 8px ${p.color}66` }} />
      <span className="absolute top-1/2 h-2.5 w-2.5 rounded-full -translate-y-1/2 -translate-x-1/2" style={{ left: `${p.fill * 100}%`, background: p.color, boxShadow: `0 0 6px ${p.color}` }} />
    </div>
    <span className="text-[12px] mono tabnum text-foreground shrink-0 w-[34px]">{p.end ?? "—"}</span>
    <span className="text-[11px] mono tabnum shrink-0 w-[40px] text-right" style={{ color: p.color }}>{p.pct}%</span>
  </div>
);

const LG = "lg:grid-cols-[42px_1.5fr_0.95fr_1.05fr_2.3fr_1.1fr_0.8fr]";

// Option 3 — enhanced recent-actions table with status badges + epoch progress.
export const RecentActionsTable = ({ rows = [], currentEpoch, onView }) => {
  if (!rows.length) return <div className="text-[13px] mono text-muted-foreground px-4 py-6 text-center">No governance actions found.</div>;
  return (
    <div data-testid="recent-actions-table">
      <div className="text-[11px] mono text-muted-foreground/70 mb-3">Detailed view with status indicators and epoch progression</div>
      <div className="rounded-xl border border-primary/15 overflow-hidden">
        <div className={cn("hidden lg:grid gap-3 px-4 py-2.5 border-b border-primary/10 bg-black/20 text-[9px] uppercase tracking-[0.14em] mono text-muted-foreground whitespace-nowrap items-center", LG)}>
          <div>#</div><div>Type</div><div>Status</div><div>Submitted</div><div>Epoch Progress</div><div>Outcome</div><div className="text-right">Actions</div>
        </div>
        {rows.map((r, i) => {
          const meta = TYPE_META[r.type] || TYPE_META._default;
          const Icon = meta.icon;
          const oc = outcomeOf(r);
          const p = progOf(r, currentEpoch);
          const idx = String(i + 1).padStart(2, "0");
          return (
            <React.Fragment key={`${r.tx_hash}:${r.cert_index}`}>
              {/* desktop */}
              <div className={cn("hidden lg:grid gap-3 items-center px-4 py-3 border-b border-primary/10 last:border-0 hover:bg-white/[0.02] transition-colors", LG)}>
                <div className="text-[11px] mono tabnum text-muted-foreground/60">{idx}</div>
                <div className="flex items-center gap-2.5 min-w-0">
                  <span className="grid place-items-center h-8 w-8 rounded-lg shrink-0" style={{ background: `${meta.color}1a`, border: `1px solid ${meta.color}55` }}>
                    <Icon size={15} weight="duotone" style={{ color: meta.color }} />
                  </span>
                  <button type="button" onClick={() => onView(r)} className="text-[12px] text-foreground hover:text-[#22d3ee] transition-colors truncate text-left" title={r.title || r.gov_action_id}>
                    {govActionLabel(r.type)}
                  </button>
                </div>
                <div><Badge tone={govStatusTone(r.status)}>{r.status}</Badge></div>
                <div className="text-[12px] mono tabnum text-muted-foreground whitespace-nowrap">epoch {r.submitted_epoch ?? "—"}</div>
                <ProgressBar p={p} />
                <div className="text-[12px] mono whitespace-nowrap" style={{ color: oc.done ? oc.color : "#94a3b8" }}>{oc.text}</div>
                <div className="flex justify-end">
                  <button type="button" data-testid={`recent-view-${r.tx_hash}`} onClick={() => onView(r)} className="inline-flex items-center gap-1.5 rounded-lg border border-primary/30 px-3 py-1.5 text-[11px] uppercase tracking-[0.1em] mono text-primary hover:border-[#22d3ee]/60 hover:text-[#22d3ee] transition-colors">
                    View <ArrowRight size={12} weight="bold" />
                  </button>
                </div>
              </div>
              {/* mobile */}
              <div className="lg:hidden px-3 py-3 border-b border-primary/10 last:border-0 space-y-2.5">
                <div className="flex items-center gap-3">
                  <span className="grid place-items-center h-9 w-9 rounded-lg shrink-0" style={{ background: `${meta.color}1a`, border: `1px solid ${meta.color}55` }}>
                    <Icon size={17} weight="duotone" style={{ color: meta.color }} />
                  </span>
                  <button type="button" onClick={() => onView(r)} className="min-w-0 flex-1 text-left">
                    <div className="text-[12px] text-foreground font-medium truncate">{govActionLabel(r.type)}</div>
                    <div className="text-[10px] mono text-muted-foreground">submitted epoch {r.submitted_epoch ?? "—"}</div>
                  </button>
                  <Badge tone={govStatusTone(r.status)}>{r.status}</Badge>
                </div>
                <ProgressBar p={p} />
                <div className="flex items-center justify-between gap-2">
                  <span className="text-[11px] mono" style={{ color: oc.done ? oc.color : "#94a3b8" }}>{oc.text}</span>
                  <button type="button" data-testid={`recent-view-m-${r.tx_hash}`} onClick={() => onView(r)} className="inline-flex items-center gap-1.5 rounded-lg border border-primary/30 px-3 py-1.5 text-[11px] uppercase tracking-[0.1em] mono text-primary">
                    View <ArrowRight size={12} weight="bold" />
                  </button>
                </div>
              </div>
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
};
